import { enableProdMode, ErrorHandler, importProvidersFrom } from '@angular/core';
import 'hammerjs';

import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { SharedModule } from './app/shared/shared.module';
import { AppRoutingModule } from './app/app-routing.module';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { RouteReuseStrategy } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { provideServiceWorker } from '@angular/service-worker';
import { ApiUrlInterceptor } from './app/core/interceptors/api-url.interceptor';
import { CorrelationIdInterceptor } from './app/core/interceptors/correlation-id.interceptor';
import { ChunkErrorHandler } from './app/core/services/error/chunk-error-handler';
import { AppErrorHandler } from './app/core/services/error/app-error-handler';
import { AuthFacade } from './app/core/facades/auth.facade';
import { AuthFacadeImpl } from './app/core/facades/auth.facade.impl';
import { provideRoleUserModals } from './app/role-user/providers/role-user-modals.provider';
import {
  isProductionBuild,
  shouldEnableSentry,
  shouldEnableServiceWorker,
} from './app/core/services/settings/environment.service';
import packageInfo from '../package.json';

// Build identity (fixed at compile time) drives Sentry, prod mode, and the service worker.
const isProd: boolean = isProductionBuild();

Sentry.init({
  dsn: 'https://d4fe4c57e0303ae7491551f3f633f89d@o4507828218036224.ingest.us.sentry.io/4507830617636864',
  enabled: shouldEnableSentry(),
  environment: environment.name,
  release: 'charity-app@' + packageInfo.version,

  integrations: [
    // Automatic performance/routing instrumentation.
    Sentry.browserTracingIntegration(),
    // Session Replay is unmasked (maskAllText: false) and would capture PII, so it runs in
    // production only — never on the deployed dev site.
    ...(isProd
      ? [
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
        ]
      : []),
  ],

  // Full tracing in production; low sample on deployed dev to limit dashboard noise.
  tracesSampleRate: isProd ? 1.0 : 0.1,

  // Dev API is api.dev.sunnystreet.com (the previous dev.api.* target never matched).
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/api\.dev\.sunnystreet\.com\/api/,
    /^https:\/\/api\.sunnystreet\.com\/api/,
  ],

  // Replay disabled outside production (integration omitted above).
  replaysSessionSampleRate: isProd ? 0.1 : 0,
  replaysOnErrorSampleRate: isProd ? 1.0 : 0,

  ignoreErrors: [
    // Injected by Instagram/Facebook in-app browsers on iOS — not from our code.
    /window\.webkit\.messageHandlers/,
  ],
});

if (isProd) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(BrowserModule, AppRoutingModule, SharedModule),
    provideAnimations(),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: ApiUrlInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CorrelationIdInterceptor, multi: true },
    // ChunkErrorHandler is provided under its own class token (not just the ErrorHandler
    // token) so AppErrorHandler can inject and delegate to a single shared instance.
    ChunkErrorHandler,
    { provide: ErrorHandler, useClass: AppErrorHandler },
    { provide: AuthFacade, useClass: AuthFacadeImpl },
    provideRoleUserModals(),
    provideIonicAngular({
      mode: 'ios', // 'ios' for ios, 'md' for android
      // useSetInputAPI: true enables Angular's ComponentRef.setInput() for modal componentProps.
      // Required for signal inputs (input.required<T>()) and enforces @Input() decorators on all
      // modal components. This is the recommended approach for Angular 18+ apps using Ionic 8.
      // See: https://ionicframework.com/docs/angular/config
      useSetInputAPI: true,
    }),
    // sw-custom.js = custom push handlers + Angular's ngsw engine via importScripts (SUN-814).
    // This URL must NEVER change: every installed client updates in place by byte-diffing it —
    // renaming the registered SW file is what stranded clients in the SUN-860 incident.
    // Enabled on dev/local as well as prod so push notifications work off production (SUN-725).
    provideServiceWorker('sw-custom.js', {
      enabled: shouldEnableServiceWorker(),
      registrationStrategy: 'registerWhenStable:5000',
    }),
  ],
}).catch(err => {
  console.error('Bootstrap error:', err);
  Sentry.captureException(err);
});